import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import logger from '@widgets/logger';
import { Typography } from '@components/Typography';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import { useTranslation } from '@hooks';
import { companyService } from '@services';
import { KB, MB } from '@constants/file';
import { Industry } from '@constants/account';
import { LocationType } from '@constants/companies';

const Appearance = ({ companyId, industry }) => {
    const { setValue, watch } = useFormContext();
    const { t } = useTranslation();
    const [uploading, setUploading] = useState({ logoUrl: false, coverUrl: false });

    const logoUrl = watch('logoUrl');
    const coverUrl = watch('coverUrl');
    const headshotUrl = watch('headshotUrl');
    const type = watch('type');

    const uploadLogo = e => async field => {
        const fieldsMap = {
            logoUrl: 'logo',
            coverUrl: 'cover',
            headshotUrl: 'headshot',
        };
        try {
            setUploading(prev => ({ ...prev, [field]: true }));
            const data = new FormData();
            data.append('file', e.target.files[0]);
            data.append('fileName', e.target.files[0].name);
            const response = await companyService.uploadCompanyImage(data, companyId, fieldsMap[field]);
            setValue(field, response.publicUrl, { shouldDirty: true });
        } catch (_) {
            logger.error(`Couldn't upload ${field}`);
        }
        setUploading(prev => ({ ...prev, [field]: false }));
        e.target.value = '';
    };

    const onDeleteLogo = field => setValue(field, null, { shouldDirty: true });

    return (
        <>
            <Typography variant="body2">{t('common.logo')}</Typography>
            <Typography
                variant="caption"
                sx={{ color: 'var(--text-secondary)', display: 'block', margin: '4px 0 8px' }}
            >
                {t('common.imageUploadFormat', { size: '500KB' })}
            </Typography>
            <ImageUploaderSingle
                maxSize={KB * 500}
                minSize={10 * KB}
                extensions="image/*"
                cropConfig={{ aspectRatio: 1, outputWidth: 250 }}
                logoUrl={logoUrl}
                disabled={uploading.logoUrl}
                onDelete={() => onDeleteLogo('logoUrl')}
                uploadLogo={e => uploadLogo(e)('logoUrl')}
                isUploading={uploading.logoUrl}
            />
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', marginTop: '8px' }}>
                {t('SettingsCompanies.coverPhoto')}
            </Typography>
            <Typography
                variant="caption"
                sx={{ color: 'var(--text-secondary)', display: 'block', margin: '4px 0 8px' }}
            >
                {t('common.imageUploadFormat', { size: '5MB' })}
            </Typography>
            <ImageUploaderSingle
                maxSize={5 * MB}
                minSize={10 * KB}
                extensions="image/*"
                cropConfig={{ aspectRatio: 16 / 9, outputMinWidth: 1110, outputMaxWidth: 2120 }}
                logoUrl={coverUrl}
                disabled={uploading.coverUrl}
                onDelete={() => onDeleteLogo('coverUrl')}
                uploadLogo={e => uploadLogo(e)('coverUrl')}
                isUploading={uploading.coverUrl}
            />
            {industry === Industry.HealthcareAndDental && type === LocationType.HealthcareProfessional && (
                <>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', marginTop: '8px' }}>
                        {t('SettingsCompanies.headshotUrl')}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: 'var(--text-secondary)', display: 'block', margin: '4px 0 8px' }}
                    >
                        {t('common.imageUploadFormat', { size: '5MB' })}
                    </Typography>
                    <ImageUploaderSingle
                        maxSize={5 * MB}
                        minSize={10 * KB}
                        extensions="image/*"
                        cropConfig={{ aspectRatio: 1, outputMinWidth: 200, outputMaxWidth: 2120 }}
                        logoUrl={headshotUrl}
                        disabled={uploading.headshotUrl}
                        onDelete={() => onDeleteLogo('headshotUrl')}
                        uploadLogo={e => uploadLogo(e)('headshotUrl')}
                        isUploading={uploading.headshotUrl}
                    />
                </>
            )}
        </>
    );
};

export default Appearance;
